import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, FileText, Star, Calendar, MapPin, Link as LinkIcon, Github, User, Clock, Trash2, Edit, AlertCircle, Check, X } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EvaluatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isRecruiter, hasRecruiterLicense } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [profile, setProfile] = useState<any>(location.state?.profile || null);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [evaluationToDelete, setEvaluationToDelete] = useState<any>(null);
  
  const [evaluation, setEvaluation] = useState({
    skills_rating: 5,
    experience_rating: 5,
    education_rating: 5,
    communication_rating: 5,
    overall_rating: 5,
    notes: '',
  });

  // Extract fromApplicants from location state
  const { fromApplicants = false } = location.state || {};

  useEffect(() => {
    // Redirect if not a recruiter
    if (!isRecruiter || !hasRecruiterLicense) {
      toast({
        variant: 'destructive',
        title: 'Access Denied',
        description: 'You must be a recruiter to access this page.',
      });
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch profile if not provided in state
        if (!profile) {
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select(`
              *,
              experiences (
                id,
                company,
                position,
                start_date,
                end_date,
                current,
                description,
                location
              ),
              education (
                id,
                institution,
                degree,
                field_of_study,
                start_date,
                end_date,
                current,
                description,
                location
              )
            `)
            .eq('id', id)
            .single();

          if (profileError) throw profileError;
          setProfile(profileData);
        }

        // Fetch evaluations
        const { data: evaluationsData, error: evaluationsError } = await supabase
          .from('candidate_evaluations')
          .select(`
            *,
            evaluator:profiles!candidate_evaluations_evaluator_id_fkey(
              id,
              full_name,
              title
            )
          `)
          .eq('profile_id', id)
          .order('created_at', { ascending: false });

        if (evaluationsError) throw evaluationsError;
        setEvaluations(evaluationsData || []);

      } catch (error: any) {
        console.error('Error fetching data:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load data',
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, user, toast, isRecruiter, hasRecruiterLicense, profile]);

  const handleAddEvaluation = async () => {
    if (!user || !profile) return;

    setEvaluationLoading(true);
    try {
      const { error } = await supabase
        .from('candidate_evaluations')
        .insert([
          {
            profile_id: profile.id,
            evaluator_id: user.id,
            ...evaluation,
            status: 'completed',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
        ]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Evaluation added successfully',
      });

      // Refresh evaluations
      const { data: newEvaluations } = await supabase
        .from('candidate_evaluations')
        .select(`
          *,
          evaluator:profiles!candidate_evaluations_evaluator_id_fkey(
            id,
            full_name,
            title
          )
        `)
        .eq('profile_id', id)
        .order('created_at', { ascending: false });

      setEvaluations(newEvaluations || []);

      // Reset form
      setEvaluation({
        skills_rating: 5,
        experience_rating: 5,
        education_rating: 5,
        communication_rating: 5,
        overall_rating: 5,
        notes: '',
      });
      setSelectedEvaluation(null);

    } catch (error: any) {
      console.error('Error adding evaluation:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to add evaluation',
      });
    } finally {
      setEvaluationLoading(false);
    }
  };

  const handleUpdateEvaluation = async () => {
    if (!user || !profile || !selectedEvaluation) return;

    setEvaluationLoading(true);
    try {
      const { error } = await supabase
        .from('candidate_evaluations')
        .update({
          ...evaluation,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedEvaluation.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Evaluation updated successfully',
      });

      // Refresh evaluations
      const { data: newEvaluations } = await supabase
        .from('candidate_evaluations')
        .select(`
          *,
          evaluator:profiles!candidate_evaluations_evaluator_id_fkey(
            id,
            full_name,
            title
          )
        `)
        .eq('profile_id', id)
        .order('created_at', { ascending: false });

      setEvaluations(newEvaluations || []);
      setSelectedEvaluation(null);
      setEvaluation({
        skills_rating: 5,
        experience_rating: 5,
        education_rating: 5,
        communication_rating: 5,
        overall_rating: 5,
        notes: '',
      });

    } catch (error: any) {
      console.error('Error updating evaluation:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update evaluation',
      });
    } finally {
      setEvaluationLoading(false);
    }
  };

  const handleDeleteEvaluation = async () => {
    if (!evaluationToDelete) return;

    try {
      const { error } = await supabase
        .from('candidate_evaluations')
        .delete()
        .eq('id', evaluationToDelete.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Evaluation deleted successfully',
      });

      // Update local state
      setEvaluations(evaluations.filter(e => e.id !== evaluationToDelete.id));

      // Reset selected evaluation if it was the one deleted
      if (selectedEvaluation?.id === evaluationToDelete.id) {
        setSelectedEvaluation(null);
        setEvaluation({
          skills_rating: 5,
          experience_rating: 5,
          education_rating: 5,
          communication_rating: 5,
          overall_rating: 5,
          notes: '',
        });
      }

    } catch (error: any) {
      console.error('Error deleting evaluation:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to delete evaluation',
      });
    } finally {
      setDeleteDialogOpen(false);
      setEvaluationToDelete(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Present';
    return format(new Date(dateString), 'MMM yyyy');
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return {
        relative: formatDistanceToNow(date, { addSuffix: true }),
        absolute: format(date, 'MMM d, yyyy h:mm a'),
      };
    } catch (error) {
      return {
        relative: 'unknown time ago',
        absolute: 'unknown date',
      };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex justify-center py-8 px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested profile could not be found or may have been deleted.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Resume */}
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Candidate Evaluation</h1>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{profile.full_name}</CardTitle>
              <CardDescription>{profile.title}</CardDescription>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm">
                {profile.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.location}</span>
                  </div>
                )}
                {profile.email && (
                  <div className="flex items-center gap-1">
                    <LinkIcon className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${profile.email}`} className="hover:text-primary">
                      {profile.email}
                    </a>
                  </div>
                )}
                {profile.github && (
                  <div className="flex items-center gap-1">
                    <Github className="h-4 w-4 text-muted-foreground" />
                    <a href={profile.github} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                      {profile.github.replace(/^https?:\/\/(www\.)?github\.com\//, '')}
                    </a>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              {profile.summary && (
                <div>
                  <h3 className="font-medium mb-2">Professional Summary</h3>
                  <p className="text-muted-foreground whitespace-pre-line">{profile.summary}</p>
                </div>
              )}

              <Separator />

              {/* Experience */}
              <div>
                <h3 className="font-medium mb-4">Work Experience</h3>
                <div className="space-y-6">
                  {profile.experiences?.map((exp: any) => (
                    <div key={exp.id}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{exp.position}</h4>
                          <p className="text-sm text-muted-foreground">{exp.company}</p>
                        </div>
                        <div className="text-sm text-right">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{formatDate(exp.start_date)} - {formatDate(exp.end_date)}</span>
                          </div>
                          {exp.location && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{exp.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {exp.description && (
                        <p className="mt-2 text-sm whitespace-pre-line">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Education */}
              <div>
                <h3 className="font-medium mb-4">Education</h3>
                <div className="space-y-6">
                  {profile.education?.map((edu: any) => (
                    <div key={edu.id}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{edu.degree}</h4>
                          <p className="text-sm text-muted-foreground">{edu.institution}</p>
                          {edu.field_of_study && (
                            <p className="text-sm">{edu.field_of_study}</p>
                          )}
                        </div>
                        <div className="text-sm text-right">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{formatDate(edu.start_date)} - {formatDate(edu.end_date)}</span>
                          </div>
                          {edu.location && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{edu.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {edu.description && (
                        <p className="mt-2 text-sm whitespace-pre-line">{edu.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Previous Evaluations */}
          {evaluations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Previous Evaluations</CardTitle>
                <CardDescription>
                  Past evaluations for this candidate
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {evaluations.map((evaluation) => (
                  <div 
                    key={evaluation.id} 
                    className={`p-4 rounded-lg border ${
                      selectedEvaluation?.id === evaluation.id ? 'bg-primary/5 border-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{evaluation.evaluator?.full_name}</p>
                          <p className="text-sm text-muted-foreground">{evaluation.evaluator?.title}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <Badge variant="outline" className="gap-1 mb-1">
                            <Clock className="h-3 w-3" />
                            {formatTimestamp(evaluation.created_at).relative}
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            {formatTimestamp(evaluation.created_at).absolute}
                          </p>
                          {evaluation.updated_at !== evaluation.created_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Updated {formatTimestamp(evaluation.updated_at).relative}
                            </p>
                          )}
                        </div>
                        {evaluation.evaluator_id === user?.id && (
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedEvaluation(evaluation);
                                setEvaluation({
                                  skills_rating: evaluation.skills_rating,
                                  experience_rating: evaluation.experience_rating,
                                  education_rating: evaluation.education_rating,
                                  communication_rating: evaluation.communication_rating,
                                  overall_rating: evaluation.overall_rating,
                                  notes: evaluation.notes || '',
                                });
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setEvaluationToDelete(evaluation);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Skills:</span>{' '}
                        <Badge variant="secondary">{evaluation.skills_rating}/10</Badge>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Experience:</span>{' '}
                        <Badge variant="secondary">{evaluation.experience_rating}/10</Badge>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Education:</span>{' '}
                        <Badge variant="secondary">{evaluation.education_rating}/10</Badge>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Communication:</span>{' '}
                        <Badge variant="secondary">{evaluation.communication_rating}/10</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Overall:</span>
                      <Badge>{evaluation.overall_rating}/10</Badge>
                    </div>
                    {evaluation.notes && (
                      <p className="mt-2 text-sm whitespace-pre-line">{evaluation.notes}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Evaluation Form */}
        <div>
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-primary" />
                {selectedEvaluation ? 'Edit Evaluation' : 'New Evaluation'}
              </CardTitle>
              <CardDescription>
                Rate this candidate's qualifications and potential fit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Skills Rating */}
              <div className="space-y-2">
                <Label>Skills Rating</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[evaluation.skills_rating]}
                    onValueChange={(value) => setEvaluation({ ...evaluation, skills_rating: value[0] })}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="w-8 text-center">
                    {evaluation.skills_rating}
                  </Badge>
                </div>
              </div>

              {/* Experience Rating */}
              <div className="space-y-2">
                <Label>Experience Rating</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[evaluation.experience_rating]}
                    onValueChange={(value) => setEvaluation({ ...evaluation, experience_rating: value[0] })}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="w-8 text-center">
                    {evaluation.experience_rating}
                  </Badge>
                </div>
              </div>

              {/* Education Rating */}
              <div className="space-y-2">
                <Label>Education Rating</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[evaluation.education_rating]}
                    onValueChange={(value) => setEvaluation({ ...evaluation, education_rating: value[0] })}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="w-8 text-center">
                    {evaluation.education_rating}
                  </Badge>
                </div>
              </div>

              {/* Communication Rating */}
              <div className="space-y-2">
                <Label>Communication Rating</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[evaluation.communication_rating]}
                    onValueChange={(value) => setEvaluation({ ...evaluation, communication_rating: value[0] })}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="w-8 text-center">
                    {evaluation.communication_rating}
                  </Badge>
                </div>
              </div>

              {/* Overall Rating */}
              <div className="space-y-2">
                <Label>Overall Rating</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    min={1}
                    max={10}
                    step={1}
                    value={[evaluation.overall_rating]}
                    onValueChange={(value) => setEvaluation({ ...evaluation, overall_rating: value[0] })}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="w-8 text-center">
                    {evaluation.overall_rating}
                  </Badge>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Evaluation Notes</Label>
                <Textarea
                  placeholder="Add notes about the candidate's qualifications and potential fit..."
                  value={evaluation.notes}
                  onChange={(e) => setEvaluation({ ...evaluation, notes: e.target.value })}
                  className="min-h-[100px]"
                />
              </div>

              {/* Submit Button */}
              <Button 
                className="w-full" 
                onClick={selectedEvaluation ? handleUpdateEvaluation : handleAddEvaluation}
                disabled={evaluationLoading}
              >
                {evaluationLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                    {selectedEvaluation ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    {selectedEvaluation ? 'Update Evaluation' : 'Save Evaluation'}
                  </>
                )}
              </Button>

              {selectedEvaluation && (
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => {
                    setSelectedEvaluation(null);
                    setEvaluation({
                      skills_rating: 5,
                      experience_rating: 5,
                      education_rating: 5,
                      communication_rating: 5,
                      overall_rating: 5,
                      notes: '',
                    });
                  }}
                >
                  Cancel Edit
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Evaluation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this evaluation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEvaluation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}